import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { registerFinalizeAfterCheckout } from '../../services/stripeWorkerClient';

const REG_CREDS_KEY = 'skimbuster_reg_credentials';
const REG_DRAFT_KEY = 'skimbuster_reg_draft';

const pageWrapSx = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#07090e',
  backgroundImage:
    'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(34, 211, 238, 0.08) 0%, transparent 50%)',
  px: 2,
};

const panelSx = {
  maxWidth: 480,
  width: '100%',
  p: 4,
  textAlign: 'center' as const,
  borderRadius: '24px',
  backgroundColor: 'background.paper',
};

export const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromRegister = params.get('from') === 'register';
  const sessionId = params.get('session_id');

  const [phase, setPhase] = useState<'idle' | 'signing_in' | 'done' | 'error'>(
    fromRegister ? 'signing_in' : 'idle'
  );
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!fromRegister) {
      setPhase('idle');
      return;
    }

    const raw = sessionStorage.getItem(REG_CREDS_KEY);
    if (!raw) {
      setPhase('error');
      setErrorMessage(
        'This browser session no longer has your sign-in details. If payment succeeded, use Log In with the email you registered, or contact support.'
      );
      return;
    }

    let email: string;
    let password: string;
    try {
      const parsed = JSON.parse(raw) as { email?: string; password?: string };
      email = String(parsed.email || '');
      password = String(parsed.password || '');
    } catch {
      setPhase('error');
      setErrorMessage('Could not read registration session. Try logging in with your email and password.');
      return;
    }

    if (!email || !password) {
      setPhase('error');
      setErrorMessage('Missing credentials. Try Log In or register again.');
      return;
    }

    let cancelled = false;

    const run = async () => {
      setPhase('signing_in');
      if (sessionId) {
        try {
          await registerFinalizeAfterCheckout(sessionId);
        } catch {
          /* worker optional; webhook may still create user */
        }
      }

      const delays = [0, 800, 1500, 2500, 4000, 6000, 8000, 10000];
      for (const ms of delays) {
        if (cancelled) return;
        if (ms > 0) await new Promise((r) => setTimeout(r, ms));
        try {
          await signInWithEmailAndPassword(auth, email, password);
          if (cancelled) return;
          sessionStorage.removeItem(REG_CREDS_KEY);
          sessionStorage.removeItem(REG_DRAFT_KEY);
          setPhase('done');
          navigate('/profile', { replace: true });
          return;
        } catch {
          /* user may not exist yet — webhook lag */
        }
      }

      if (!cancelled) {
        setPhase('error');
        setErrorMessage(
          'Payment may still be processing your account. Wait a minute, then use Log In with the same email and password. If it still fails, contact support.'
        );
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [fromRegister, sessionId, navigate]);

  const isSettingsFlow = !fromRegister;

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={4} sx={panelSx}>
        {fromRegister && phase === 'signing_in' && (
          <>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Finishing your account…
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Payment received. Signing you in (this can take a few seconds).
            </Typography>
            <CircularProgress size={36} sx={{ my: 2 }} />
          </>
        )}

        {fromRegister && phase === 'error' && (
          <>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Almost there
            </Typography>
            {errorMessage && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                {errorMessage}
              </Alert>
            )}
            <Button variant="contained" onClick={() => navigate('/login')} fullWidth sx={{ mb: 1 }}>
              Go to Log In
            </Button>
            <Button variant="text" onClick={() => navigate('/register')} fullWidth>
              Start registration again
            </Button>
          </>
        )}

        {isSettingsFlow && (
          <>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Payment successful
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your subscription will update in a moment. You can review billing and invoices anytime from
              Platform Settings.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/settings')} fullWidth>
              Back to Settings
            </Button>
          </>
        )}

        {fromRegister && phase === 'done' && (
          <Typography color="text.secondary">Redirecting…</Typography>
        )}
      </Paper>
    </Box>
  );
};

export const SubscriptionCancel: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromRegister = params.get('from') === 'register';

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={4} sx={panelSx}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Checkout canceled
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {fromRegister
            ? 'No payment was made and no SkimBuster account was created. You can return to the registration summary to try again (your browser may still have your details on the previous step).'
            : 'No changes were made. You can choose a plan again from Platform Settings whenever you are ready.'}
        </Typography>
        {fromRegister ? (
          <>
            <Button variant="contained" onClick={() => navigate('/register/summary')} fullWidth sx={{ mb: 1 }}>
              Back to registration summary
            </Button>
            <Button variant="text" onClick={() => navigate('/register')} fullWidth>
              Start over
            </Button>
          </>
        ) : (
          <Button variant="outlined" onClick={() => navigate('/settings')} fullWidth>
            Platform Settings (billing)
          </Button>
        )}
      </Paper>
    </Box>
  );
};
